import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const ORDER_STATUSES = [
  { value: 'pending', label: 'En attente', color: 'bg-amber-100 text-amber-700' },
  { value: 'confirmed', label: 'Confirmée', color: 'bg-blue-100 text-blue-700' },
  { value: 'preparing', label: 'En préparation', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'delivering', label: 'En livraison', color: 'bg-purple-100 text-purple-700' },
  { value: 'delivered', label: 'Livrée', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'cancelled', label: 'Annulée', color: 'bg-rose-100 text-rose-700' },
];

const PAYMENT_STATUSES = [
  { value: 'pending', label: 'En attente' },
  { value: 'paid', label: 'Payée' },
  { value: 'refunded', label: 'Remboursée' },
];

export const AdminOrdersPage = () => {
  const { axiosInstance } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  const fetchOrders = useCallback(async () => {
    const params = filter === 'all' ? {} : { status: filter };
    const res = await axiosInstance.get('/admin/orders', { params });
    setOrders(res.data);
  }, [axiosInstance, filter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const updateStatus = async (order_id, order_status) => {
    try {
      await axiosInstance.patch(`/admin/orders/${order_id}`, { order_status });
      toast.success('Statut mis à jour');
      fetchOrders();
    } catch {
      toast.error('Erreur');
    }
  };

  const updatePayment = async (order_id, order_status, payment_status) => {
    try {
      await axiosInstance.patch(`/admin/orders/${order_id}`, { order_status, payment_status });
      toast.success('Paiement mis à jour');
      fetchOrders();
    } catch {
      toast.error('Erreur');
    }
  };

  return (
    <div data-testid="admin-orders">
      <h1 className="font-heading text-3xl font-bold text-[#3E2723]">Commandes</h1>
      <p className="text-[#6D4C41] mt-1 mb-6">{orders.length} commande(s)</p>

      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setFilter('all')} className={`px-3 py-1.5 rounded-full text-sm font-medium ${filter === 'all' ? 'bg-[#3E2723] text-white' : 'bg-white text-[#3E2723] border border-[#D4C4AA]/60'}`}>Toutes</button>
        {ORDER_STATUSES.map((s) => (
          <button key={s.value} onClick={() => setFilter(s.value)} className={`px-3 py-1.5 rounded-full text-sm font-medium ${filter === s.value ? 'bg-[#3E2723] text-white' : 'bg-white text-[#3E2723] border border-[#D4C4AA]/60'}`}>{s.label}</button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-[#D4C4AA]/40 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F5EFE3] text-[#6D4C41]">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Commande</th>
              <th className="text-left px-4 py-3 font-medium">Client</th>
              <th className="text-left px-4 py-3 font-medium">Livraison</th>
              <th className="text-left px-4 py-3 font-medium">Montant</th>
              <th className="text-left px-4 py-3 font-medium">Paiement</th>
              <th className="text-left px-4 py-3 font-medium">Statut</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => {
              const status = ORDER_STATUSES.find((s) => s.value === o.order_status) || ORDER_STATUSES[0];
              const isOpen = expandedId === o.order_id;
              return (
                <React.Fragment key={o.order_id}>
                  <tr className="border-t border-[#F5EFE3] hover:bg-[#F5EFE3]/30 cursor-pointer" onClick={() => setExpandedId(isOpen ? null : o.order_id)} data-testid={`admin-order-${o.order_id}`}>
                    <td className="px-4 py-3">
                      <p className="font-mono text-xs text-[#8B7355]">{o.order_id.slice(0, 16)}…</p>
                      <p className="text-xs text-[#6D4C41]">{new Date(o.created_at).toLocaleString('fr-CH')}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-[#3E2723]">{o.user?.name || '—'}</p>
                      <p className="text-xs text-[#6D4C41]">{o.user?.email || '—'}</p>
                    </td>
                    <td className="px-4 py-3 text-[#3E2723]">
                      <p>{o.delivery_slot?.date}</p>
                      <p className="text-xs text-[#6D4C41]">{o.delivery_slot?.time_slot}</p>
                    </td>
                    <td className="px-4 py-3 font-semibold text-[#3E2723]">{o.total_amount.toFixed(2)} CHF</td>
                    <td className="px-4 py-3">
                      <select value={o.payment_status || 'pending'} onChange={(e) => { e.stopPropagation(); updatePayment(o.order_id, o.order_status, e.target.value); }} onClick={(e) => e.stopPropagation()} className="text-xs bg-transparent border border-[#D4C4AA]/60 rounded px-2 py-1">
                        {PAYMENT_STATUSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                      <p className="text-xs text-[#8B7355] mt-1">{o.payment_method}</p>
                    </td>
                    <td className="px-4 py-3">
                      <select value={o.order_status} onChange={(e) => { e.stopPropagation(); updateStatus(o.order_id, e.target.value); }} onClick={(e) => e.stopPropagation()} className={`text-xs rounded-full px-3 py-1 font-medium ${status.color} border-0`} data-testid={`admin-order-status-${o.order_id}`}>
                        {ORDER_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-[#F5EFE3]/40">
                      <td colSpan={6} className="px-6 py-4">
                        <div className="space-y-2">
                          <p className="text-xs uppercase tracking-wider text-[#8B7355]">Articles</p>
                          {o.items.map((item, idx) => (
                            <div key={`${o.order_id}-${item.dish_id}-${idx}`} className="flex justify-between text-sm">
                              <span>{item.quantity} × {item.name}</span>
                              <span className="text-[#6D4C41]">{item.item_total.toFixed(2)} CHF</span>
                            </div>
                          ))}
                          {o.delivery_address && (
                            <p className="text-xs text-[#6D4C41] mt-3"><strong>Adresse :</strong> {o.delivery_address}</p>
                          )}
                          {o.special_instructions && (
                            <p className="text-xs text-[#6D4C41]"><strong>Instructions :</strong> {o.special_instructions}</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {orders.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#6D4C41]/60">Aucune commande</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
